import React, { useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";
import AdminOandaProTrader from "@/components/adminone/AdminOandaProTrader";

/**
 * CareLogix™ ORACLE — AdminOne OANDA Control Center (Phase 22 upgrade).
 *
 * Tab structure (additive — preserves all legacy Status / Prices / Test):
 *   • OVERVIEW : status + diagnostics + test (legacy view)
 *   • PRICES   : instrument price table (legacy view)
 *   • PRO TRADER : new full Pro Trader suite (Phase 22)
 *
 * Strictly additive — no original feature has been removed.
 */
export default function AdminOandaCenter() {
  const [tab, setTab] = useState("overview");
  const [status, setStatus] = useState(null);
  const [prices, setPrices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const [s, p] = await Promise.all([adminApi.oandaStatus(), adminApi.oandaPrices()]);
      setStatus(s.data); setPrices(p.data?.prices || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const onTest = async () => {
    setBusy(true); setTestResult(null);
    try { const { data } = await adminApi.oandaTest(); setTestResult(data); }
    finally { setBusy(false); }
  };

  const connected = !!status?.configured;

  return (
    <div className="admin-page" data-testid="admin-oanda-center">
      <div className="admin-page__head">
        <h2 className="admin-page__title">OANDA Control Center</h2>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="oracle-btn oracle-btn--ghost oracle-btn--sm" onClick={load} disabled={busy} data-testid="admin-oanda-refresh">
            {busy ? "…" : "↻ Refresh"}
          </button>
          <button className="oracle-btn oracle-btn--gold oracle-btn--sm" onClick={onTest} disabled={busy} data-testid="admin-oanda-test">
            Run Live Test
          </button>
        </div>
      </div>

      {/* Tab nav (additive) */}
      <nav className="admin-fx__tabs" role="tablist" aria-label="OANDA Control Center" style={{ marginTop: 8 }}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "overview"}
          className={`admin-fx__tab ${tab === "overview" ? "is-active" : ""}`}
          onClick={() => setTab("overview")}
          data-testid="admin-oanda-tab-overview"
        >Overview</button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "prices"}
          className={`admin-fx__tab ${tab === "prices" ? "is-active" : ""}`}
          onClick={() => setTab("prices")}
          data-testid="admin-oanda-tab-prices"
        >Prices</button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "pro"}
          className={`admin-fx__tab ${tab === "pro" ? "is-active" : ""}`}
          onClick={() => setTab("pro")}
          data-testid="admin-oanda-tab-pro"
          title="Bloomberg-grade Pro Trader experience with LOGIX-2 intelligence"
        >Pro Trader™</button>
      </nav>

      {/* OVERVIEW TAB */}
      {tab === "overview" && (
        <div data-testid="admin-oanda-overview">
          {!connected && (
            <div className="oracle-empty" style={{ padding: 14 }}>
              OANDA keys required. Add <code>OANDA_API_KEY</code> and <code>OANDA_ACCOUNT_ID</code> to backend environment.
            </div>
          )}
          <div className="admin-metric-grid" style={{ marginTop: 10 }}>
            <div className="admin-metric"><div className="admin-metric__label">Configured</div><div className="admin-metric__value">{connected ? "YES" : "NO"}</div></div>
            <div className="admin-metric"><div className="admin-metric__label">Account ID</div><div className="admin-metric__value" style={{ fontSize: 14 }}>{status?.account_id_masked || "—"}</div></div>
            <div className="admin-metric"><div className="admin-metric__label">Environment</div><div className="admin-metric__value" style={{ fontSize: 14 }}>{(status?.environment || "—").toUpperCase()}</div></div>
            <div className="admin-metric"><div className="admin-metric__label">Instruments</div><div className="admin-metric__value">{(status?.instruments || []).length}</div></div>
          </div>

          {testResult && (
            <div className="oracle-empty" style={{ padding: 14, marginTop: 10 }} data-testid="admin-oanda-test-result">
              Test result: <b>{testResult.ok ? "OK" : "FAILED"}</b> &middot; status: <code>{testResult.snapshot?.status}</code>
            </div>
          )}
        </div>
      )}

      {/* PRICES TAB (legacy table preserved) */}
      {tab === "prices" && (
        <table className="admin-table" style={{ marginTop: 14 }} data-testid="admin-oanda-prices-table">
          <thead><tr><th>Instrument</th><th>Bid</th><th>Ask</th><th>Spread</th><th>Status</th><th>Timestamp</th></tr></thead>
          <tbody>
            {prices.map((p, i) => (
              <tr key={i}>
                <td><b>{p.instrument}</b></td>
                <td>{p.bid ?? "—"}</td>
                <td>{p.ask ?? "—"}</td>
                <td>{p.spread ?? "—"}</td>
                <td>{(p.status || "—").toUpperCase()}</td>
                <td>{p.timestamp || "—"}</td>
              </tr>
            ))}
            {prices.length === 0 && <tr><td colSpan={6} className="oracle-empty" style={{ padding: 14 }}>No instrument data.</td></tr>}
          </tbody>
        </table>
      )}

      {/* PRO TRADER TAB (additive) */}
      {tab === "pro" && (
        <AdminOandaProTrader />
      )}
    </div>
  );
}
